import { ArrowLeftIcon } from '@heroicons/react/20/solid'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useLazyQuery } from '@apollo/client'
import { useState, useEffect } from 'react'
import { GET_MEALS_LIST } from 'graphql/common/queries'
import loadingImage from '../../../assets/images/boat-loading.gif'

export default function MealDetails() {
    const { id } = useParams()
    console.log('Meal ID: ', id)
    const { t } = useTranslation()
    const [mealInfo, setMealInfo] = useState(null)

    const [getMealList, { loading: mealLoading, error: mealError }] = useLazyQuery(GET_MEALS_LIST, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        onCompleted: data => {
            if (data.searchMeal.success && data.searchMeal.code === '200') {
                setMealInfo(data.searchMeal.meals[0])
            }
        },
    })

    useEffect(() => {
        getMealList({
            variables: {
                page: {
                    limit: 1,
                    offset: 0,
                },
                searchMealId: id,
            },
        })
    }, [id])

    return (
        <>
            <div className="mb-6 flex justify-between items-center">
                <div className="flex">
                    <button
                        onClick={() => {
                            window.history.back()
                        }}
                        type="button"
                        className="inline-flex items-center gap-x-1 rounded-md text-sm text-gray-600"
                    >
                        <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-4 rtl:rotate-180" />
                        {t('Back')}
                    </button>
                    <h3 className="text-lg font-semibold leading-7 text-primary ps-2 pb-1">{t('Meal Details')}</h3>
                </div>
                <a
                    href={`/meals/meal-form/${mealInfo?.meal_id}`}
                    className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                >
                    {t('Edit Meal')}
                </a>
            </div>

            {mealLoading && (
                <div className="flex flex-col justify-center items-center bg-white h-96 mt-16">
                    <img alt="logo" src={loadingImage} className="w-[250px]" />
                    <p className="pt-3 text-primary font-semibold">Loading, please wait...</p>
                </div>
            )}

            {mealError && (
                <div className="flex justify-center items-center bg-white py-5">
                    <div className="text-lg text-center">{t('Failed to fetch meal details!')}</div>
                </div>
            )}

            {!mealLoading && !mealError && mealInfo && (
                <div className="flex flex-wrap items-end justify-between gap-4">
                    <div className="max-sm:w-full sm:flex-1">
                        <div className="mt-5">
                            <div className="px-4 sm:px-0">
                                <h3 className="text-lg font-semibold leading-7 text-primary">
                                    {t('Meal Description')}
                                </h3>
                            </div>

                            <div className="mt-3 border-t border-gray-100">
                                <dl className="divide-y divide-gray-100">
                                    <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('English Description')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {mealInfo.description.en}
                                        </dd>
                                    </div>
                                    <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Arabic Description')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {mealInfo.description.ar}
                                        </dd>
                                    </div>
                                </dl>
                            </div>
                        </div>

                        <div className="mt-8">
                            <div className="px-4 sm:px-0">
                                <h3 className="text-lg font-semibold leading-7 text-primary">
                                    {t('Price Information')}
                                </h3>
                            </div>
                            <div className="mt-3 border-t border-gray-100">
                                <dl className="divide-y divide-gray-100">
                                    <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Base Price')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {mealInfo.price.base}
                                        </dd>
                                    </div>
                                    <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">{t('Tax')}</dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {mealInfo.price.tax}
                                        </dd>
                                    </div>
                                    <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Total Price')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            <span className="font-semibold">
                                                {(
                                                    parseFloat(mealInfo.price.base) + parseFloat(mealInfo.price.tax)
                                                ).toFixed(2)}
                                            </span>
                                        </dd>
                                    </div>
                                </dl>
                            </div>
                        </div>

                        <div className="mt-8">
                            <div className="px-4 sm:px-0">
                                <h3 className="text-lg font-semibold leading-7 text-primary">{t('Status')}</h3>
                            </div>
                            <div className="mt-3 border-t border-gray-100">
                                <dl className="divide-y divide-gray-100">
                                    <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Active Status')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            <span
                                                className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                                                    mealInfo.active
                                                        ? 'bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20'
                                                        : 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-600/20'
                                                }`}
                                            >
                                                {mealInfo.active ? t('Active') : t('Inactive')}
                                            </span>
                                        </dd>
                                    </div>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}
